import { useState } from 'react';
import { easeInOut, useReducedMotion } from 'framer-motion';
import type { StatisticDto } from '@portfolio/shared';
import { formatNumber } from '@/lib/utils';
import { useScrollPin } from '../hooks';
import { useLocale } from '../i18n';

/** Scroll distance (in viewport heights) the section stays pinned while the numbers count. */
const PIN_VH = 0.9;
/** The count finishes a little before the pin lets go, so the final numbers hold for a beat. */
const COUNT_END = 0.85;

// satz.framer.website's "Stat Countdown": every digit rolls up from 0 to its own value in a
// clipped slot, each 0.1s after the one before, over 1.6s (0.8 × its 2s duration) with
// easeInOut, after a 0.2s delay. Here scrolling plays that timeline instead of the clock.
const ROLL_DELAY = 0.2;
const ROLL_STAGGER = 0.1;
const ROLL_DURATION = 1.6;
/** Height of a digit's clip window (0.85em line + 0.15em above and below): one roll step. */
const SLOT_EM = 1.15;
const countDigits = (text: string) => text.replace(/\D/g, '').length;
const rollLength = (digits: number) =>
  ROLL_DELAY + Math.max(0, digits - 1) * ROLL_STAGGER + ROLL_DURATION;

/** `text` with each digit at its point of the roll `time` seconds in. */
function RollingNumber({ text, time }: { text: string; time: number }) {
  let digit = -1;
  return [...text].map((ch, i) => {
    if (ch < '0' || ch > '9') return <span key={i}>{ch}</span>;
    digit += 1;
    const local = (time - ROLL_DELAY - digit * ROLL_STAGGER) / ROLL_DURATION;
    const value = local >= 1 ? Number(ch) : easeInOut(Math.max(0, local)) * Number(ch);
    const current = Math.floor(value);
    const frac = value - current;
    return (
      // The invisible final digit sizes the slot, so the layout never moves while rolling. The
      // clip window reaches past it (padding undone by negative margins) because the tight
      // tracking and line height let the black glyphs overflow their box.
      <span
        key={i}
        className="relative -mx-[0.1em] -my-[0.15em] inline-block overflow-hidden px-[0.1em] py-[0.15em]"
      >
        <span className="invisible">{ch}</span>
        <span
          className="absolute inset-x-[0.1em] inset-y-[0.15em] text-center"
          style={{ transform: `translateY(${-frac * SLOT_EM}em)` }}
        >
          {current}
        </span>
        {frac > 0 && (
          <span
            className="absolute inset-x-[0.1em] inset-y-[0.15em] text-center"
            style={{ transform: `translateY(${(1 - frac) * SLOT_EM}em)` }}
          >
            {current + 1}
          </span>
        )}
      </span>
    );
  });
}

function Stat({ stat, time }: { stat: StatisticDto; time: number }) {
  return (
    <li className="flex flex-col items-center text-center">
      <p
        className="flex items-start"
        aria-label={`${stat.prefix ?? ''}${stat.value}${stat.suffix ?? ''} ${stat.label}`}
      >
        {stat.prefix && (
          <span
            aria-hidden
            className="mt-[0.5em] -me-[0.04em] text-[clamp(3rem,1rem+7.35vw,6.875rem)] leading-none font-extralight text-silver"
          >
            {stat.prefix}
          </span>
        )}
        <span
          aria-hidden
          className="text-[clamp(4.6rem,1rem+12.9vw,11.3rem)] leading-[0.85] font-black tracking-[-0.015em] text-strong tabular-nums"
        >
          <RollingNumber text={formatNumber(stat.value)} time={time} />
        </span>
        {stat.suffix && (
          <span
            aria-hidden
            className="mt-[0.5em] -me-[0.04em] text-[clamp(3rem,1rem+7.35vw,6.875rem)] leading-none font-extralight text-silver"
          >
            {stat.suffix}
          </span>
        )}
      </p>
      <p
        aria-hidden
        className="mt-[9px] max-w-[9ch] text-balance text-[clamp(2.2rem,1rem+4vw,4.1875rem)] leading-[0.87] font-light tracking-[-0.01em] text-strong"
      >
        {/* keep "&" with the word after it: "Clients / & Brands" */}
        {stat.label.replace(/&\s+/g, '& ')}
      </p>
    </li>
  );
}

/**
 * Key numbers. The section pins in the middle of the screen while the visitor scrolls, and the
 * scrolling rolls every digit up from 0 (satz-style); once they are all reached the page moves on.
 * The count only runs forward: scrolling back up doesn't roll the numbers down again.
 * Reduced-motion visitors get the final numbers and no pin.
 */
export function Stats({ stats }: { stats: StatisticDto[] }) {
  const { t } = useLocale();
  const reduce = useReducedMotion();
  const pinned = !reduce && stats.length > 0;
  const [progress, setProgress] = useState(0);
  const { outer, inner, outerStyle, innerStyle } = useScrollPin({
    enabled: pinned,
    distance: (vh) => vh * PIN_VH,
    align: 'center',
    // Plays once: scrolling back up leaves the counted numbers in place.
    onProgress: (p) => setProgress((prev) => Math.max(prev, p)),
  });
  if (!stats.length) return null;
  const counted = pinned ? Math.min(1, progress / COUNT_END) : 1;
  const time =
    counted * rollLength(Math.max(...stats.map((s) => countDigits(formatNumber(s.value)))));

  return (
    <div ref={outer} style={outerStyle}>
      <section
        ref={inner}
        aria-label={t.keyNumbers}
        style={innerStyle}
        className={pinned ? 'container-site sticky' : 'container-site'}
      >
        <div className="h-px bg-line" />
        <ul className="grid grid-cols-1 gap-14 py-16 sm:grid-cols-3 sm:gap-6 md:-mx-[30px] md:pt-[62px] md:pb-[112px]">
          {stats.map((s) => (
            <Stat key={s.id} stat={s} time={time} />
          ))}
        </ul>
      </section>
    </div>
  );
}
